import { Button } from "@app/ui";
import { useEffect, useMemo, useState } from "react";
import { useI18n } from "./useI18n.js";

/** Rows rendered per page by {@link usePagedRows}. One screen holds ~20. */
export const LIST_PAGE = 100;

/**
 * Cap a list render at a page of rows, with an explicit "show more".
 *
 * Every task list in this app is un-virtualized, and the datasets are real:
 * an inbox of 2,600 undated tasks or an All-tasks list of 8,900 rows paints
 * over a hundred thousand DOM nodes and blocks the main thread for a second or
 * more — on a route change, or on every keystroke in search. Filtering is
 * cheap; painting is not. Nobody scrolls past the first screen, so render a
 * page and keep the honest total next to the control.
 *
 * `resetKey` is whatever makes the list a *different* list (a query, a sort, a
 * filter selection): paging back to the first page on that change is the point,
 * otherwise a new query inherits the old expansion.
 */
export function usePagedRows<T>(rows: readonly T[], resetKey: unknown, pageSize = LIST_PAGE) {
  const [limit, setLimit] = useState(pageSize);
  useEffect(() => { setLimit(pageSize); }, [resetKey, pageSize]);
  const shown = useMemo(() => (rows.length <= limit ? (rows as T[]) : rows.slice(0, limit)), [rows, limit]);
  return {
    shown,
    total: rows.length,
    hasMore: rows.length > shown.length,
    showMore: () => setLimit((n) => n + pageSize),
  };
}

/** The count + "show more" control that belongs under a {@link usePagedRows} list. */
export function ShowMoreFooter({ shown, total, onShowMore, className = "" }: {
  shown: number;
  total: number;
  onShowMore: () => void;
  className?: string;
}) {
  const { t } = useI18n();
  if (shown >= total) return null;
  return (
    <div className={`flex items-center justify-between gap-3 border-t px-3 py-2 ${className}`}>
      <span className="text-xs text-muted-foreground">
        {t("list.showing", { shown: String(shown), total: String(total) })}
      </span>
      <Button variant="outline" size="sm" onClick={onShowMore}>{t("list.showMore")}</Button>
    </div>
  );
}
